import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { TSP } from '../../../core/models';

export interface CityFormData {
  city?: TSP.Internal.Dictionary.City.Details;
  countries: TSP.Internal.Dictionary.Country.Item[];
  regions: TSP.Internal.Dictionary.Region.Item[];
}

@Component({
  selector: 'app-city-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButton,
    MatChipGrid,
    MatChipInput,
    MatChipRemove,
    MatChipRow,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
  ],
  templateUrl: './city-form.html',
  styleUrl: './city-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CityForm implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<CityForm>);
  protected readonly data = inject<CityFormData>(MAT_DIALOG_DATA);

  public readonly separatorKeysCodes = [ENTER, COMMA] as const;

  public countries: TSP.Internal.Dictionary.Country.Item[] = [];
  public regions: TSP.Internal.Dictionary.Region.Item[] = [];

  public form = new FormGroup({
    enName: new FormControl<string | null>(null, [Validators.required]),
    ruName: new FormControl<string | null>(null),
    ukName: new FormControl<string | null>(null, [Validators.required]),
    countryId: new FormControl<number | null>(null, [Validators.required]),
    regionId: new FormControl<number | null>(null),
    synonyms: new FormControl<string[]>([]),
  });

  public get isEditMode(): boolean {
    return !!this.data?.city;
  }

  public get synonyms(): string[] {
    return this.form.value.synonyms ?? [];
  }

  public get filteredRegions(): TSP.Internal.Dictionary.Region.Item[] {
    const selectedCountryId = this.form.value.countryId;
    if (!selectedCountryId) {
      return this.regions;
    }
    return this.regions.filter((region) => region.countryId === selectedCountryId);
  }

  public ngOnInit(): void {
    this.countries = this.data.countries;
    this.regions = this.data.regions;

    if (this.data?.city) {
      this.form.patchValue({
        enName: this.data.city.enName,
        ruName: this.data.city.ruName,
        ukName: this.data.city.ukName,
        countryId: this.data.city.countryId,
        regionId: this.data.city.regionId ?? null,
        synonyms: [...(this.data.city.synonyms ?? [])],
      });
    }
  }

  public addSynonym(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.form.patchValue({ synonyms: [...this.synonyms, value] });
    }
    event.chipInput.clear();
  }

  public removeSynonym(synonym: string): void {
    this.form.patchValue({ synonyms: this.synonyms.filter((s) => s !== synonym) });
  }

  public onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();

    const mode: 'create' | 'update' = this.isEditMode ? 'update' : 'create';
    const payload: TSP.Internal.Dictionary.City.CreatePayload | TSP.Internal.Dictionary.City.UpdatePayload = {
      id: this.data.city?.id ?? undefined,
      enName: raw.enName,
      ruName: raw.ruName,
      ukName: raw.ukName,
      countryId: raw.countryId,
      regionId: raw.regionId ?? undefined,
      synonyms: raw.synonyms,
    };

    this.dialogRef.close({ type: mode, payload });
  }

  public onCancel(): void {
    this.dialogRef.close(null);
  }
}
